import { useState } from 'react';

import { ArrowRight, TrendingUp } from 'lucide-react';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';

import type { components } from 'wg-api-sdk';

import PlatformLink from '@/components/common/PlatformLink';
import { Button } from '@/components/shadcn/button';
import hasItems from '@/lib/utils/hasItems';

import RecommendationTitle from './RecommendationTitle';
import SignalIcon from './SignalIcon';

type Props = {
  recommendation: components['schemas']['OnesuranceRecommendation'];
};

const MAX_RECOMMENDATIONS = 4;
const MIN_CONVERSION_PROBABILITY = 50;

export default function SalesRecommendation({ recommendation }: Props) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const allAdvice = recommendation.advice ?? [];

  const relevantAdvice = allAdvice
    .filter(
      (advice) =>
        (advice.conversion_probability ?? 0) >= MIN_CONVERSION_PROBABILITY,
    )
    .sort(
      (a, b) =>
        (b.conversion_probability ?? 0) - (a.conversion_probability ?? 0),
    );

  const visibleAdvice = showAll
    ? relevantAdvice
    : relevantAdvice.slice(0, MAX_RECOMMENDATIONS);

  const handleActionClick = (
    advice: components['schemas']['OnesuranceAdvice'],
  ) => {
    posthog.capture('Onesurance sales action clicked', {
      level: recommendation.level,
      advice: advice.title,
      conversionProbability: advice.conversion_probability,
    });
  };

  return (
    <div className="flex flex-col gap-1">
      <RecommendationTitle recommendation={recommendation} />

      {recommendation.description && (
        <p className="text-muted-foreground text-xs">
          {recommendation.description}
        </p>
      )}

      <div className="flex flex-col items-start gap-2">
        {hasItems(visibleAdvice) && (
          <ul className="text-muted-foreground mt-2 flex w-full flex-col gap-1 text-xs">
            {visibleAdvice.map((advice) => {
              const action = advice.actions?.find(
                (a) => a.type === 'CALCULATE_QUOTE',
              );

              return (
                <li
                  key={advice.title}
                  className="flex flex-wrap items-center justify-between gap-2 leading-none"
                >
                  <div className="flex items-center gap-2 leading-none">
                    {advice.conversion_probability ? (
                      <SignalIcon probability={advice.conversion_probability} />
                    ) : (
                      <TrendingUp className="size-4 shrink-0" />
                    )}

                    {advice.title}
                  </div>

                  {action && (
                    <Button variant="secondary" size="xs" asChild>
                      <PlatformLink
                        to={action.url ?? ''}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={() => handleActionClick(advice)}
                      >
                        {action.title}

                        <ArrowRight />
                      </PlatformLink>
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {allAdvice.length > MAX_RECOMMENDATIONS && (
          <Button
            onClick={() => {
              posthog.capture('Onesurance show all recommendations toggled', {
                showAll: !showAll,
                type: recommendation.type,
              });
              setShowAll(!showAll);
            }}
            variant="ghost"
            size="xs"
          >
            {showAll
              ? t('RECOMMENDATION_ADVICE_SHOW_LESS')
              : t('RECOMMENDATION_ADVICE_SHOW_ALL', {
                  count: allAdvice.length,
                })}
          </Button>
        )}
      </div>
    </div>
  );
}
